/**
 * Flush timing + auto-retry policy for the event outbox.
 * After max auto attempts, the row is held for the Sync now button.
 */
import { OutboxRecord } from './offlineEventStore';

export type FlushFailureKind =
    | 'validation-submitted'
    | 'auth'
    | 'retry'
    | 'terminal'
    | 'rejected'
    | 'delete-succeeded'
    | 'update-missing';

/** Abort a hung Arena call so the spinner cannot spin forever. */
export const FLUSH_HTTP_MS = 5000;

/** Shown while the outbox will not POST because Arena missed its pings. */
export const WAITING_FOR_ARENA = 'Save is waiting for Arena';

/**
 * Run one flush HTTP call. The AbortSignal fires when the budget is spent,
 * so the browser drops the request instead of leaving it open.
 */
export const abortable = <T>(
    ms: number,
    work: (signal: AbortSignal) => Promise<T>,
    message: string
): Promise<T> => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), ms);
    return work(ctrl.signal)
        .finally(() => clearTimeout(timer))
        .catch((err: unknown) => {
            if (ctrl.signal.aborted) {
                throw new Error(message);
            }
            throw err;
        });
};

/** Row left in "flushing" longer than this is treated as a failed attempt. */
export const FLUSH_STUCK_MS = 7000;

/**
 * Wait out one flush call. A success that arrives after FLUSH_HTTP_MS still
 * counts. The call is aborted only at FLUSH_STUCK_MS, which is when the row
 * may be sent again. Aborting at FLUSH_HTTP_MS dropped a 201 that landed a
 * fraction later and left "did not respond in time" on screen.
 */
export const awaitFlush = <T>(
    work: (signal: AbortSignal) => Promise<T>,
    message: string
): Promise<T> => abortable(FLUSH_STUCK_MS, work, message);

/** Automatic flush tries before holding for manual Sync now. */
export const MAX_AUTO_ATTEMPTS = 3;

export const retryOrWaitingMessage = (canFlush: boolean, attempts: number): string =>
    canFlush ? `Retrying save (${attempts}/${MAX_AUTO_ATTEMPTS})` : WAITING_FOR_ARENA;

export interface FlushAttemptResult {
    attempts: number;
    /** True once attempts hit MAX_AUTO_ATTEMPTS - stop auto-poll, wait for Sync now. */
    holdForManual: boolean;
}

/** Bump attempt count after a timed-out / failed flush. */
export const bumpFlushAttempt = (attempts: number | undefined): FlushAttemptResult => {
    const next = (attempts ?? 0) + 1;
    return { attempts: next, holdForManual: next >= MAX_AUTO_ATTEMPTS };
};

const terminalStatuses = new Set([400, 422]);

/**
 * 410 ends a validation attempt permanently.
 * 401/403 stay on the login path. 400/404/409/422 are not retried.
 * Delete 404 means the row is already gone. Update 404 drops that row.
 * 408, 429, and 5xx keep the auto-retry budget. Network errors never reach this.
 */
export const classifyFlushFailure = (op: OutboxRecord['op'], status: number): FlushFailureKind => {
    if (status === 410) return 'validation-submitted';
    if (status === 401 || status === 403) return 'auth';
    if (status === 404 && op === 'delete') return 'delete-succeeded';
    if (status === 404 && op === 'update') return 'update-missing';
    if (status === 409) return 'rejected';
    if (status === 404 || terminalStatuses.has(status)) return 'terminal';
    return 'retry';
};

/** Create and update say "Event saved". Delete stays quiet here; the screen says it was deleted. */
export const flushSuccessMessage = (op: OutboxRecord['op']): string | null =>
    op === 'delete' ? null : 'Event saved';

/** A second delete reuses the queued row. Pending updates for that id are cancelled. */
export const planDeleteEnqueue = (
    rows: OutboxRecord[],
    serverEventId: number
): { reuseId: string | null; cancelIds: string[] } => {
    const sameId = rows.filter((row) => row.serverEventId === serverEventId);
    const reuse = sameId.find(
        (row) => row.op === 'delete' && (row.status === 'pending' || row.status === 'flushing')
    );
    const cancelIds = sameId
        .filter((row) => row.op === 'update' && row.status === 'pending')
        .map((row) => row.id);
    return { reuseId: reuse ? reuse.id : null, cancelIds };
};
